'use client';

/**
 * Devise d'affichage choisie par le viewer (EUR / USD), tenue client-side et
 * persistée en `localStorage` pour survivre d'une visite à l'autre. Défaut USD.
 * Le choix s'applique à tous les prix du catalog ; les deux montants (EUR/USD)
 * arrivent dans les DTO, ce provider décide seulement lequel afficher.
 */

import {
    createContext,
    type ReactNode,
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';

export type Currency = 'EUR' | 'USD';

const DEFAULT_CURRENCY: Currency = 'USD';

const STORAGE_KEY = 'catalog-currency-v1';

function isCurrency(value: string | null): value is Currency {
    return value === 'EUR' || value === 'USD';
}

type CurrencyContextValue = {
    currency: Currency;
    setCurrency: (currency: Currency) => void;
};

const CurrencyContext = createContext<CurrencyContextValue | null>(null);

export function CurrencyProvider({ children }: { children: ReactNode }) {
    const [currency, setCurrencyState] = useState<Currency>(DEFAULT_CURRENCY);
    const hydrated = useRef(false);

    // Hydrate from localStorage after mount (avoids SSR/client mismatch).
    useEffect(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            if (isCurrency(raw)) setCurrencyState(raw);
        } catch {
            // Storage unavailable → keep the default currency.
        }
        hydrated.current = true;
    }, []);

    useEffect(() => {
        if (!hydrated.current) return;
        try {
            localStorage.setItem(STORAGE_KEY, currency);
        } catch {
            // Storage full/unavailable — the choice stays in-memory.
        }
    }, [currency]);

    const setCurrency = useCallback((next: Currency) => {
        setCurrencyState(next);
    }, []);

    const value = useMemo<CurrencyContextValue>(
        () => ({ currency, setCurrency }),
        [currency, setCurrency],
    );

    return <CurrencyContext.Provider value={value}>{children}</CurrencyContext.Provider>;
}

export function useCurrency(): CurrencyContextValue {
    const ctx = useContext(CurrencyContext);
    if (!ctx) {
        throw new Error('useCurrency must be used within a CurrencyProvider');
    }
    return ctx;
}
